import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Spinner } from '../../components/ui/Spinner';
import type { Account } from '../../types/account';
import {
  PORTAL_PERMISSION_LABELS,
  type PortalPermission,
  type PortalUser,
} from '../../types/portalUser';
import {
  deletePortalUser,
  listPortalUsersForAccount,
  resendPortalInvite,
  updatePortalUser,
} from './api';
import { InvitePortalUserModal } from './InvitePortalUserModal';
import { EditPortalPermissionsModal } from './EditPortalPermissionsModal';
import { canEditPermissions, permissionSavedMessage } from '../../lib/portalPermissionEdit';

// CLIENT PORTAL — who can see this account, on the account page.
//
// ⚠️ The list includes people whose contact is linked to an ANCESTOR of this
// account (a group link, which is how most access is given). A staff member on a
// location page who sees an empty list would otherwise either invite a second
// person to something already covered, or conclude nobody can see it when
// someone can — both worse than a slightly longer list with the grant labelled.
//
// ⚠️⚠️ WHAT EACH PERSON SEES COMES FROM THEIR CONTACT'S ACCOUNT LINKS, not from
// anything editable here. So there is no "change what they can see" control on
// this list: that edit belongs on the contact, and a second one here would be
// the stored-copy drift the 2026-09-02 change removed. Disable still revokes
// outright, and still wins over every link the contact holds.
//
// ⚠️⚠️ FEATURES (permissions) ARE EDITABLE HERE (2026-10-08) — and they are the
// PERSON'S, not this account's. One list on the user, applied to every location
// they can see. The edit dialog leads with that sentence and the count; the row
// button says "Edit features", never anything account-shaped. Not offered for a
// disabled user (see canEditPermissions).

type Props = { account: Account };

function statusBadge(user: PortalUser) {
  if (user.status === 'disabled') return <Badge variant="destructive">Disabled</Badge>;
  if (user.status === 'invited') return <Badge variant="secondary">Invite sent</Badge>;
  return <Badge variant="outline">Active</Badge>;
}

function formatWhen(iso: string | null): string {
  if (!iso) return 'never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'unknown';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function permissionSummary(permissions: PortalPermission[]): string {
  if (permissions.length === 0) return 'No features';
  return permissions.map((p) => PORTAL_PERMISSION_LABELS[p]).join(', ');
}

/**
 * ⚠️ How many locations this person's contact links actually resolve to — the
 * number an AM needs to see before assuming a group contact only reads one club.
 * Zero is a real and visible state: a contact whose links were all removed keeps
 * a portal record and sees nothing.
 */
function coverageSummary(locationCount: number): string {
  if (locationCount === 0) return 'no locations';
  return locationCount === 1 ? '1 location' : `${locationCount} locations`;
}

export function PortalAccessSection({ account }: Props) {
  const [inviting, setInviting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<PortalUser | null>(null);
  const [editing, setEditing] = useState<PortalUser | null>(null);
  const queryClient = useQueryClient();
  const queryKey = ['portal-users', account.id];

  const usersQuery = useQuery({
    queryKey,
    queryFn: () => listPortalUsersForAccount(account.id),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'disabled' }) =>
      updatePortalUser(id, { status }),
    onSuccess: (user) => {
      refresh();
      toast.success(
        user.status === 'disabled'
          ? `${user.displayName} can no longer sign in.`
          : `${user.displayName} can sign in again.`,
      );
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const resend = useMutation({
    mutationFn: (id: string) => resendPortalInvite(id),
    onSuccess: () => toast.success('Sign-in link sent.'),
    onError: (err) => toast.error((err as Error).message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deletePortalUser(id),
    onSuccess: () => {
      setConfirmDelete(null);
      refresh();
      toast.success('Portal access removed.');
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const users = usersQuery.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-slate-600 max-w-prose">
          Client contacts who can sign in to the portal and see this account. They sign in with an
          emailed link — there is no password. What each of them can see follows their contact's
          account links.
        </p>
        <div className="flex shrink-0 items-center gap-2">
          {/* Round 2, item 7 — the entry point to "view as client", from the
              page where an AM is already thinking about this client's access. */}
          <Button variant="outline" size="sm" asChild>
            <Link to={`/tools/client-report?accountId=${encodeURIComponent(account.id)}`}>
              View as client
            </Link>
          </Button>
          <Button size="sm" onClick={() => setInviting(true)}>
            Give access
          </Button>
        </div>
      </div>


      {usersQuery.isLoading ? (
        <Spinner />
      ) : usersQuery.error ? (
        <p className="text-sm text-rose-600">
          Couldn't load portal access: {(usersQuery.error as Error).message}
        </p>
      ) : users.length === 0 ? (
        <p className="text-sm text-slate-500">Nobody has portal access to this account.</p>
      ) : (
        <ul className="divide-y divide-slate-200 rounded-md border border-slate-200">
          {users.map((user) => {
            // ⚠️ Derived from the CONTACT'S links, so the badge tracks reality
            // rather than a snapshot taken whenever they were first invited.
            const viaAncestor = !user.grantedAccountIds.includes(account.id);
            return (
              <li key={user.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900 truncate">
                      {user.displayName}
                    </span>
                    {statusBadge(user)}
                    {viaAncestor && (
                      // ⚠️ Labelled, not hidden: the AM needs to know that
                      // editing this person's access changes what they see on
                      // OTHER accounts too.
                      <Badge variant="secondary">via a parent account</Badge>
                    )}
                  </div>
                  <div className="text-sm text-slate-600 truncate">{user.email}</div>
                  <div className="text-xs text-slate-500">
                    {permissionSummary(user.permissions)} · {coverageSummary(user.locationCount)} ·
                    last signed in {formatWhen(user.lastLoginAt)} · invited by{' '}
                    {user.invitedByName}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {canEditPermissions(user.status) && (
                    <Button variant="ghost" size="sm" onClick={() => setEditing(user)}>
                      Edit features
                    </Button>
                  )}
                  {user.status !== 'disabled' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => resend.mutate(user.id)}
                      disabled={resend.isPending}
                    >
                      Resend link
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setStatus.mutate({
                        id: user.id,
                        status: user.status === 'disabled' ? 'active' : 'disabled',
                      })
                    }
                    disabled={setStatus.isPending}
                  >
                    {user.status === 'disabled' ? 'Re-enable' : 'Disable'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-rose-600 hover:text-rose-700"
                    onClick={() => setConfirmDelete(user)}
                  >
                    Remove
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {inviting && (
        <InvitePortalUserModal
          account={account}
          onClose={() => setInviting(false)}
          onDone={refresh}
        />
      )}

      {editing && (
        <EditPortalPermissionsModal
          user={editing}
          accountName={account.name}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setEditing(null);
            // ⚠️ The PREFIX, not this account's key: the same person appears on
            // every account page they can see, and the change is theirs on all
            // of them.
            void queryClient.invalidateQueries({ queryKey: ['portal-users'] });
            toast.success(permissionSavedMessage(updated.displayName, updated.locationCount));
          }}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-1 text-lg font-semibold text-slate-900">Remove portal access?</h2>
            <p className="mb-4 text-sm text-slate-600">
              {confirmDelete.displayName} ({confirmDelete.email}) will no longer be able to sign
              in. Their existing sign-in links stop working. The contact record stays.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setConfirmDelete(null)}
                disabled={remove.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => remove.mutate(confirmDelete.id)}
                disabled={remove.isPending}
              >
                {remove.isPending ? 'Removing…' : 'Remove access'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ⚠️⚠️ THE "Show the new report layout" TICK IS GONE (§4, 2026-09-04). Scott:
// *"There is still a tick for Show the new report layout, this should be removed
// as we only have the one style."* It had done nothing for days —
// `restyledReportEnabled` returns true for any non-empty scope — so it was a
// control that read as a choice and made none.
//
// ⚠️⚠️ THE CONTROL WENT; THE DATA DID NOT. `Account.portalReportRestyleEnabled`
// is still on the type, the wire schema and every stored document, ON PURPOSE:
// those flags are what make `restyleFlag.ts`'s documented one-line revert still
// work. Removing the confusing switch and removing the escape hatch are
// different changes, and only the first was asked for.
//
// ⚠️⚠️ AND THE OLD REPORT PATH IS NOT DEAD CODE. It renders the EMPTY-SCOPE
// explanation, which the restyle has none of — `RestyledReport` returns null
// with no report, i.e. a blank page. So a client whose grants were revoked or
// misconfigured is told what happened and pointed at a human, and that is kept
// alive by `ReportPages`' own `accounts.length === 0` guard rather than by any
// flag. Deleting the old path needs the restyle to own an empty state FIRST
// (Phase 2, its own round); doing it here would turn a revoked grant into a
// blank page.
